import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { useStore } from '../context/StoreContext';
import { useLocale } from '../context/LocaleContext';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleLogin = () => {
  const redirectUrl = window.location.origin + '/';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

// Extract minutes from a "Try again in N minutes." message for a nicer countdown UX
const extractLockoutMinutes = (msg) => {
  if (typeof msg !== 'string') return null;
  const m = msg.match(/in\s+(\d+)\s+minutes?/i);
  return m ? parseInt(m[1], 10) : null;
};

const AuthModal = ({ open, onClose }) => {
  const { user, login, register, logout } = useStore();
  const { t } = useLocale();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lockoutSec, setLockoutSec] = useState(0);

  useEffect(() => {
    if (lockoutSec <= 0) return;
    const t = setInterval(() => setLockoutSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [lockoutSec]);

  const reset = () => { setEmail(''); setPassword(''); setName(''); setError(''); setBusy(false); };
  const close = () => { onClose(); reset(); };

  const submit = async (e) => {
    e.preventDefault();
    if (lockoutSec > 0) return;
    setBusy(true);
    setError('');
    try {
      if (mode === 'register') {
        await register(email.trim(), password, name.trim());
      } else {
        await login(email.trim(), password);
      }
      close();
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      if (status === 429) {
        const mins = extractLockoutMinutes(detail) || 15;
        setLockoutSec(mins * 60);
        setError(`Too many attempts. Try again in ${mins} minute${mins === 1 ? '' : 's'}.`);
      } else {
        setError(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  if (user) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-sm" data-testid="auth-account-modal">
          <DialogHeader><DialogTitle>{t('myAccount')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              {user.picture && <img src={user.picture} alt="" className="w-10 h-10 rounded-full" />}
              <div>
                <p className="text-sm">{t('welcomeBack')} <span className="font-semibold">{user.name}</span></p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
            </div>
            <Link to="/my-orders" onClick={close} data-testid="account-my-orders-link" className="block w-full text-center bg-black text-white py-2 text-sm font-bold tracking-wider rounded hover:bg-gray-800 transition">MY ORDERS</Link>
            <Button data-testid="auth-logout-btn" className="w-full bg-[#22c55e] hover:bg-[#16a34a]" onClick={async () => { await logout(); close(); }}>{t('logout')}</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="auth-modal">
        <VisuallyHidden><DialogTitle>Sign in to Feron Store</DialogTitle></VisuallyHidden>
        <div className="bg-black text-white p-6 flex items-center gap-4">
          <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_feron-collection/artifacts/41yxnxq7_Untitled%20design.webp" alt="Feron" className="h-16 w-auto object-contain" style={{ filter: 'brightness(1.15)' }} />
          <div>
            <div className="text-xs tracking-widest text-[#39ff14]">{t('welcomeTo')}</div>
            <div className="text-xl font-black">{t('brandName')}</div>
            <div className="text-xs mt-1 opacity-80">{mode === 'register' ? 'Create your account' : 'Sign in to continue'}</div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <button
            type="button"
            onClick={startGoogleLogin}
            data-testid="auth-google-btn"
            className="w-full flex items-center justify-center gap-2 border-2 border-black hover:bg-black hover:text-white transition py-2.5 rounded font-bold text-sm tracking-wider"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.56c2.08-1.92 3.28-4.75 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.77c-.98.66-2.24 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z" fill="#34A853"/>
              <path d="M5.84 14.1a6.6 6.6 0 010-4.2V7.07H2.18a11 11 0 000 9.86l3.66-2.83z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.65l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative py-1">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-widest"><span className="bg-white px-2 text-gray-500">or use email</span></div>
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === 'register' && (
              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1 block">NAME</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="auth-name-input" placeholder="Your name" required />
              </div>
            )}
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">EMAIL</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="auth-email-input" placeholder="you@example.com" required autoComplete="email" />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">PASSWORD</label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="auth-password-input" placeholder="At least 6 characters" required minLength={6} autoComplete={mode === 'register' ? 'new-password' : 'current-password'} />
            </div>
            {error && <p className="text-xs text-red-600 font-medium" data-testid="auth-error">{error}</p>}
            {lockoutSec > 0 && (
              <p className="text-xs text-amber-700 font-semibold" data-testid="auth-lockout-timer">
                Locked out — retry available in {Math.floor(lockoutSec / 60)}:{String(lockoutSec % 60).padStart(2, '0')}
              </p>
            )}
            <Button type="submit" disabled={busy || lockoutSec > 0} data-testid="auth-submit-btn" className="w-full bg-[#22c55e] hover:bg-[#16a34a] font-bold tracking-wider">
              {busy ? '...' : (lockoutSec > 0 ? `WAIT ${Math.floor(lockoutSec / 60)}:${String(lockoutSec % 60).padStart(2, '0')}` : (mode === 'register' ? 'CREATE ACCOUNT' : 'SIGN IN'))}
            </Button>
          </form>

          <div className="text-center text-xs">
            {mode === 'register' ? (
              <>Already have an account?{' '}
                <button data-testid="auth-switch-login" onClick={() => { setMode('login'); setError(''); }} className="text-[#22c55e] font-bold">Sign in</button>
              </>
            ) : (
              <>New to Feron?{' '}
                <button data-testid="auth-switch-register" onClick={() => { setMode('register'); setError(''); }} className="text-[#22c55e] font-bold">Create account</button>
              </>
            )}
          </div>

          <p className="text-[11px] text-gray-500 leading-relaxed">{t('termsText')}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
